import React from 'react';
import PropTypes from 'prop-types';
import styles from './timer.css';
import classnames from 'classnames';
import ComponentsUtil, {LogLevel} from '../../utils/ComponentsUtil';
import Tooltip from 'cd-ui-react/components/tooltip';

/**
 * React Timer component responsible for starting
 * a timer and displaying specified time unit elpased
 * in given display format.
 */
export default class Timer extends React.Component {
	constructor (props) {
		super(props);
		this.state = {
			timeUnitElapsed: props.startTime,
			prevTickTimeStamp: null
		};
		this.interruptedTimeInSeconds = 0;
	}

	formatElapsedTime () {
		let {displayFormat, intervalInMillis, dynamicFormat} = this.props;
		let formattedTime = '';
		if (displayFormat === 'hh:mm:ss') {
			let timeElapsed = this.state.timeUnitElapsed - this.interruptedTimeInSeconds;
			let date = new Date(timeElapsed  * intervalInMillis);
			let hours = date.getUTCHours() < 10 ? '0' + date.getUTCHours() : date.getUTCHours();
			let minutes = date.getUTCMinutes() < 10 ? '0' + date.getUTCMinutes() : date.getUTCMinutes();
			let seconds = date.getSeconds() < 10 ? '0' + date.getSeconds() : date.getSeconds();
			if (dynamicFormat) {
				if (hours >= 1) {
					formattedTime = hours + ':' + minutes + ':' + seconds;
				} else {
					formattedTime = minutes + ':' + seconds;
				}
			} else {
				formattedTime = hours + ':' + minutes + ':' + seconds;
			}
		} else if (displayFormat === 'mm:ss') {
			// let timeElapsed = this.props.isInterrupted ? this.state.timeUnitElapsed : (this.state.timeUnitElapsed -  this.state.interruptedTimeInSeconds);			  
			let timeElapsed = this.state.timeUnitElapsed - this.interruptedTimeInSeconds;

			let date = new Date(timeElapsed * intervalInMillis);
			let minutes = date.getUTCMinutes() < 10 ? '0' + date.getUTCMinutes() : date.getUTCMinutes();
			let seconds = date.getSeconds() < 10 ? '0' + date.getSeconds() : date.getSeconds();
			formattedTime = minutes + ':' + seconds;
		}

		return formattedTime;
	}

	/**
	 * Method to update timer state on every scheduled
	 * timer interval
	 */
	tick = () => {
		if (this.props.isInterrupted) {
			this.interruptedTimeInSeconds = this.interruptedTimeInSeconds + 1;
			return;
		}
		this.setState((prevState) => {
			const currTimeStamp = Math.round(new Date().getTime() / 1000);
			const prevTickTimeStamp = prevState.prevTickTimeStamp || currTimeStamp;
			if (this.props.type === 'COUNT_DOWN') {
				if (prevState.timeUnitElapsed <= 0) {
					this.stopTimer();
				}
				let timeUnitElapsed = prevState.timeUnitElapsed - (currTimeStamp - prevTickTimeStamp);
				timeUnitElapsed = timeUnitElapsed > 0 ? timeUnitElapsed : 0;

				return {
					timeUnitElapsed: timeUnitElapsed,
					prevTickTimeStamp: currTimeStamp
				};
			}
			return {
				timeUnitElapsed: (prevState.timeUnitElapsed  + (currTimeStamp - prevTickTimeStamp )),
				prevTickTimeStamp: currTimeStamp
			};
		});
	}

	/**
	 * Method to schedule timer at specified interval.
	 * This will not schedule a timer if it is already scheduled.
	 */
	startTimer () {
		if (this.props.intervalInMillis === 1000) {
			ComponentsUtil.subscribeToHubTopic(
				window.finesse.containerservices.ContainerServices.Topics.TIMER_TICK_EVENT,
				this.tick);
		} else if (!this.interval) {
			this.interval = setInterval(this.tick, this.props.intervalInMillis);
		}
	}

	/**
	 * Method to stop timer interval if it is already
	 * scheduled. Does nothing if no timer interval is
	 * scheduled.
	 */
	stopTimer (startTime = 0) {
		if (this.interval) {
			clearInterval(this.interval);
			this.interval = undefined;
		} else {
			ComponentsUtil.unsubscribeToHubTopic(
				window.finesse.containerservices.ContainerServices.Topics.TIMER_TICK_EVENT,
				this.tick);
		}
		this.state.timeUnitElapsed = startTime;
		this.state.prevTickTimeStamp = null;
	}

	/**
	 * React lifycycle method to stop timer if resetTimer
	 * props is being sent as tru.
	 *
	 * @param {Object} newProps
	 */
	UNSAFE_componentWillReceiveProps (newProps) {
		ComponentsUtil.log(`Timer-${this.props.id}`, LogLevel.DEBUG,
			`Timer will receive new props. Should reset timer ${newProps.reset}`);
		if (newProps.reset) this.stopTimer(newProps.startTime);
	}

	componentWillUnmount () {
		this.stopTimer();
	}
	/**
	 * React render lifycycle method to render component on DOM.
	 */
	render () {
		let {id, show, reset, className} = this.props;
		if (show || reset) {
			this.startTimer();
			let formattedTime = this.formatElapsedTime();
			const timerElement = (
				<div id={id}
					role="timer"
					aria-label={[this.props.tooltipText, formattedTime].join(' ')}
					className={classnames(`${styles.timer}`, className)} >
					{formattedTime}
				</div>
			);
			if (this.props.tooltipText) {
				return (
					<Tooltip id={`timer-tooltip-${id}`}
						show
						chevron placement="right"
						content={this.props.tooltipText}
					>
						{timerElement}
					</Tooltip>);
			} else {
				return (timerElement);
			}
		} else {
			this.stopTimer();
			return null;
		}
	};
}

/**
 * Properties exposed by Timer
 */
Timer.propTypes = {
	/** id for timer div */
	id: PropTypes.string.isRequired,
	/** interval at which to schedule timer tick */
	intervalInMillis: PropTypes.number,
	/**
	 * time format for time to
	 * be displayed in
	 */
	displayFormat: PropTypes.string,
	/**
	 * Whether to show or hide the timer
	 */
	show: PropTypes.bool,
	/**
	 * Whether to reset the timer
	 */
	reset: PropTypes.bool,
	/**
	 * CSS classes to be applied to the timer
	 */
	className: PropTypes.string,
	/**
	 * Start time of the timer
	 */
	startTime: PropTypes.number,
	/**
	 * Type of Timer running up or down
	 * COUNT_UP starts from 0 or given time
	 * COUNT_DOWN starts from given time till 0
	 * */
	type: PropTypes.string,

	// Tooltip text to be shown; truthy values => display tooltip; falsy values => no tooltip.
	tooltipText: PropTypes.string,

	dynamicFormat: PropTypes.bool
};

/**
 * Default properties values for Timer if
 * not provided by the caller.
 */
Timer.defaultProps = {
	/**
	 * Default time format for time to
	 * be displayed in timer
	 */
	displayFormat: 'hh:mm:ss',
	/**
	 * Default interval at which to schedule timer tick.
	 */
	intervalInMillis: 1000,
	/**
	 * Don't display timer by default
	 */
	show: false,
	/**
	 * Don't reset timer by default
	 */
	reset: false,
	/**
	 * Start time of the timer
	 */
	startTime: 0,
	/**
	 * Timer running up or down
	 * COUNT_UP starts from 0 or given time
	 * COUNT_DOWN starts from given time till 0
	 * */
	type: 'COUNT_UP',

	// By default, no tooltip
	tooltipText: null,

	dynamicFormat: false
};
